"use client";

import { AgentThreadVo } from "@/api";
import { BackIcon } from "@/assets/icons";
import Button from "@/component/Button";
import { Loading } from "@/component/Loading";
import { ThreadListItem } from "@/component/ThreadListItem";
import { ThreadListItemSkeleton } from "@/component/ThreadListItem/skeleton";
import { fetchUserThreads } from "@/service/threads";
import { userStore } from "@/store/user";
import "@ant-design/v5-patch-for-react-19";
import { useRouter } from "@bprogress/next/app";
import { Avatar, Dropdown } from "antd";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef } from "react";
import useSWRInfinite from "swr/infinite";
import styles from "./page.module.css";

const PAGE_SIZE = 20;

const getKey = (
  pageIndex: number,
  previousPageData: AgentThreadVo[] | null,
) => {
  if (previousPageData && !previousPageData.length) return null;
  return { offset: pageIndex * PAGE_SIZE, limit: PAGE_SIZE, type: "user" };
};

export default observer(function ThreadListPage() {
  const user = userStore.user;
  const router = useRouter();

  const { data, setSize, isValidating, isLoading, error } = useSWRInfinite<
    AgentThreadVo[]
  >(getKey, (params) => fetchUserThreads(params), {
    revalidateFirstPage: false,
    persistSize: true,
  });

  const threads = useMemo(() => (data ? data.flat() : []), [data]);

  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    userStore.fetchUser().catch((error) => {
      console.error("Failed to fetch user:", error);
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !isValidating &&
          !isLoading &&
          !error
        ) {
          setSize((s) => s + 1);
        }
      },
      {
        rootMargin: "200px",
        threshold: 0,
      },
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [setSize, isValidating, isLoading, error]);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  return (
    <div className="relative min-h-screen w-full bg-background-container">
      <header className="sticky top-0 h-[60px] px-5 sm:px-11 flex items-center justify-between border-b border-b-[rgba(16,16,16,0.12)] backdrop-blur-sm z-[10] bg-background-elevated/80 text-text-primary">
        <Button
          type="plain"
          className="text-xl"
          onClick={handleBack}
          icon={<BackIcon className="w-4 h-4" />}
        >
          <span>Back to start</span>
        </Button>

        {user && (
          <Dropdown
            trigger={["click"]}
            overlayClassName={styles.userMenu}
            menu={{
              items: [
                {
                  label: (
                    <a href="/auth/logout">
                      <div className="flex items-center gap-2 font-medium leading-[18px]">
                        Sign Out
                      </div>
                    </a>
                  ),
                  key: "logout",
                },
              ],
            }}
          >
            <div className="flex items-center gap-3 cursor-pointer transition-opacity duration-200 hover:opacity-80">
              <Avatar size={36} src={user.avatarUrl} className="rounded-full" />
              <span className="text-text-primary text-sm">{user.nickname}</span>
            </div>
          </Dropdown>
        )}
      </header>

      <div className="w-full px-5 sm:px-11 relative pt-10 pb-20 mx-auto">
        <h1 className="text-text-primary text-xl font-bold mb-5">
          Recent Projects
        </h1>

        {isLoading && threads.length === 0 ? (
          <div className="grid-thread">
            {Array.from({ length: 10 }).map((_, idx) => (
              <ThreadListItemSkeleton key={idx} />
            ))}
          </div>
        ) : (
          <div className="grid-thread">
            {threads.map((item) => (
              <Link href={`/thread/${item.id}`} key={item.id}>
                <ThreadListItem thread={item} />
              </Link>
            ))}
          </div>
        )}

        <div ref={loaderRef} className="flex justify-center p-8 min-h-[100px]">
          {isValidating || isLoading ? (
            <Loading fontSize={16} color="var(--color-text-primary)" />
          ) : (
            <div className="h-1" />
          )}
        </div>
      </div>
    </div>
  );
});
